import React, { Component } from 'react';
import { Form, Input, Spin, Tooltip } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled/macro';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { LinkInput } from 'components';
import { AlertActions, ProjectsActions } from 'redux/actions';
import pickBy from 'loadsh/pickBy';
import identity from 'loadsh/identity';
import isEmpty from 'loadsh/isEmpty';
import moment from 'moment/moment';

import {
  Container,
  formLayout,
  formValidateMessages,
  H1,
  H2,
  FormWrapper,
  SpaceBetween,
  SecondaryButton,
  PrimaryButtonV2,
  AddFormItemLabel,
  AddSaveHint,
  addMetaData,
} from 'utils';
import RichText from './RichText';
import DatePickerButton from './DatePickerNew';

const FormItem = Form.Item;

const EXTRA_TEXT_STYLE = { paddingRight: 60, whiteSpace: 'normal' };
const TEXT_AREA_STYLE = {
  height: '40px',
  fontSize: '16px',
  resize: 'none',
};
const INPUT_STYLE = { height: '40px', fontSize: '16px' };

const AddTextArea = styled(Input.TextArea)`
  .ant-input {
    font-size: 16px;
    padding-top: 7px;
  }
`;

const getStoredJson = (storage, key) => {
  const value = storage.getItem(key);
  return value ? JSON.parse(value) : {};
};

class BasicInformation extends Component {
  formRef = React.createRef();

  static propTypes = {
    updateDraft: PropTypes.func.isRequired,
    project: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    isUniq: PropTypes.bool.isRequired,
    checkUrl: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    projectId: PropTypes.string.isRequired,
    currentStep: PropTypes.number.isRequired,
    setCanMove: PropTypes.func.isRequired,
    showModal: PropTypes.bool.isRequired,
    selectedStep: PropTypes.number.isRequired,
    setSubmitFromModal: PropTypes.func.isRequired,
    handleCreateProject: PropTypes.func.isRequired,
    handleExitEditForm: PropTypes.func.isRequired,
    editProject: PropTypes.bool.isRequired,
  };

  state = {
    creating: false,
    isEvent: false,
    showLoginModal: false,
  };

  onFinish = (values) => {
    const fundingParsed = getStoredJson(sessionStorage, 'fundingData');
    sessionStorage.removeItem('fundingData');

    const categoryParsed = getStoredJson(sessionStorage, 'categoryType');
    sessionStorage.removeItem('categoryType');

    const userTypeParsed = getStoredJson(sessionStorage, 'userType');
    sessionStorage.removeItem('userType');

    const user = localStorage.getItem('user');
    if (!user) {
      AlertActions.error('Bitte melde dich an, um fortzufahren.', 'Fail');
      return;
    }

    const parsedUser = JSON.parse(user);
    const metaData = addMetaData(this.props.project);
    const formValues = metaData ? { ...values, meta_data: metaData } : values;

    const vals = {
      ...formValues,
      url: `${values.url}`.toLowerCase(),
      ...fundingParsed,
      ...categoryParsed,
      ...userTypeParsed,
      initiator_email: parsedUser.email,
      initiator_first_name: parsedUser.first_name,
      initiator_last_name: parsedUser.last_name,
    };

    if (this.state.isEvent) {
      const bookingEndDate = moment(values.funding_end_date).endOf('day');
      const eventDate = moment(values.event_date).startOf('day');
      const projectStartTimestamp =
        this.props.project?.funding_start_date ||
        this.props.project?.created_at;
      const projectStartDate = projectStartTimestamp
        ? moment.unix(Number(projectStartTimestamp)).startOf('day')
        : moment().startOf('day');

      vals.funding_end_date = bookingEndDate.unix();
      vals.event_date = eventDate.unix();
      vals.funding_days = Math.max(
        bookingEndDate.diff(projectStartDate, 'days'),
        1
      );
    }

    const {
      updateDraft,
      projectId,
      currentStep,
      handleCreateProject,
      editProject,
    } = this.props;

    if (projectId) {
      updateDraft(projectId, vals, currentStep);
      this.props.handleSteps(editProject ? 3 : 5);
      return;
    }

    this.setState({ creating: true });
    const error = handleCreateProject(vals);
    if (error) {
      this.setState({ creating: false });
      AlertActions.error(error, 'Fail');
    }
  };

  handleTitleChange = (e) => {
    this.formRef.current?.setFieldsValue({
      url: this.formatURL(e.target.value),
    });
  };

  formatURL = (title) => {
    return title
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 15);
  };

  getContent = (content, name) => {
    this.formRef.current.setFieldsValue({ [name]: content });
  };

  onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const form = event.target.form;
      const index = Array.prototype.indexOf.call(form, event.target);
      form.elements[index + 1]?.focus();
    }
  };

  onReturn = () => {
    if (this.props.editProject) {
      this.props.handleExitEditForm();
      return;
    }

    this.props.handleSteps(this.state.isEvent ? 2 : 3);
  };

  componentDidMount() {
    const username = JSON.parse(localStorage.getItem('user'))?.first_name;

    if (this.props.project && this.props.projectId) {
      this.setState({
        isEvent: this.props?.project?.fundingTarget?.is_ticketable ?? false,
      });
    } else {
      try {
        const categoryType = getStoredJson(sessionStorage, 'categoryType');
        this.setState({ isEvent: categoryType.project_type === 'event' });
      } catch {
        this.setState({ isEvent: false });
      }
    }

    if (!username) {
      this.setState({ showLoginModal: true });
    }
  }

  getProjectTypeLabel = () => (this.state.isEvent ? 'Event' : 'Projekt');

  renderTextArea = ({ placeholder, onChange }) => (
    <AddTextArea
      placeholder={placeholder}
      rows={1}
      showCount
      maxLength={100}
      onChange={onChange}
      style={TEXT_AREA_STYLE}
      onKeyDown={this.onKeyDown}
    />
  );

  renderTitleField = () => {
    const projectType = this.getProjectTypeLabel();
    const isCreateMode = !this.props.projectId;

    return (
      <FormItem
        name="title"
        label={<AddFormItemLabel>Titel des {projectType}s</AddFormItemLabel>}
        required={false}
        extra={
          <div style={EXTRA_TEXT_STYLE}>
            Gib deinem {projectType} einen aussagekräftigen Titel.
          </div>
        }
        rules={[
          {
            required: true,
            message: 'Titel ist erforderlich',
          },
        ]}
      >
        {this.renderTextArea({
          placeholder: `Titel des ${projectType}s`,
          onChange: isCreateMode ? this.handleTitleChange : undefined,
        })}
      </FormItem>
    );
  };

  renderLocationField = () => {
    const isEvent = this.state.isEvent;
    const label = isEvent ? 'Veranstaltungsort' : 'Wirkungsort';

    return (
      <FormItem
        name="event_location"
        label={<AddFormItemLabel>{label}</AddFormItemLabel>}
        extra={
          isEvent
            ? 'Der Ort, an dem dein Event stattfindet.'
            : 'Der Ort, an dem dein Projekt was bewirkt.'
        }
        required={false}
      >
        {this.renderTextArea({ placeholder: label })}
      </FormItem>
    );
  };

  renderEventFields = () => (
    <>
      {this.renderLocationField()}

      <FormItem
        name="event_date"
        label={<AddFormItemLabel>Veranstaltungsdatum</AddFormItemLabel>}
        extra="Der Tag, an dem dein Event stattfindet."
        required={false}
        rules={[
          {
            required: true,
            message: 'Veranstaltungsdatum ist erforderlich',
          },
        ]}
      >
        <DatePickerButton placeholder="Veranstaltungsdatum auswählen" />
      </FormItem>

      <FormItem
        name="funding_end_date"
        label={<AddFormItemLabel>Buchungsschluss</AddFormItemLabel>}
        extra="Bis zu diesem Tag können Tickets gebucht werden."
        required={false}
        dependencies={['event_date']}
        rules={[
          {
            required: true,
            message: 'Buchungsschluss ist erforderlich',
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const eventDate = getFieldValue('event_date');

              if (!value || !eventDate || !value.isAfter(eventDate, 'day')) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error(
                  'Der Buchungsschluss darf nicht nach dem Veranstaltungsdatum liegen.'
                )
              );
            },
          }),
        ]}
      >
        <DatePickerButton placeholder="Buchungsschluss auswählen" />
      </FormItem>
    </>
  );

  renderFundingDaysField = (disabled = false) => (
    <FormItem
      name="funding_days"
      label={
        <AddFormItemLabel>Finanzierungszeitraum in Tagen</AddFormItemLabel>
      }
      required={false}
      extra="Maximal 90 Tage"
      rules={[
        {
          required: true,
          message: 'Finanzierungszeitraum ist erforderlich',
        },
      ]}
    >
      <Input
        placeholder="30 (empfohlen)"
        type="number"
        min={1}
        max={90}
        disabled={disabled}
        style={disabled ? INPUT_STYLE : TEXT_AREA_STYLE}
        onKeyDown={disabled ? undefined : this.onKeyDown}
      />
    </FormItem>
  );

  renderProjectFields = () => {
    if (!this.props.editProject) {
      return (
        <>
          {this.renderLocationField()}
          {this.renderFundingDaysField()}
        </>
      );
    }

    return (
      <>
        {this.renderLocationField()}
        <Tooltip
          title="Falls du das ändern möchtest, wende dich an unser Support-Team."
          placement="bottom"
          color="#000"
          overlayInnerStyle={{
            color: '#fff',
            borderRadius: '8px',
          }}
          arrow={true}
        >
          {this.renderFundingDaysField(true)}
        </Tooltip>
      </>
    );
  };

  renderCampaignDetailsFields = () =>
    this.state.isEvent ? this.renderEventFields() : this.renderProjectFields();

  renderStoryFields = () => {
    const projectType = this.getProjectTypeLabel();

    return (
      <>
        <H2>Erzähle uns deine Geschichte</H2>
        <p>
          Erkläre prägnant, worum es in deinem {projectType} geht, warum dein
          Vorhaben wichtig ist - und weshalb andere dich unterstützen sollten.
        </p>

        <FormItem
          name="subtitle"
          label={<AddFormItemLabel>Kurzbeschreibung</AddFormItemLabel>}
          required={false}
          extra={
            <div style={EXTRA_TEXT_STYLE}>
              Die Mission deines {projectType}s in einem Satz.
            </div>
          }
          rules={[
            {
              required: true,
              message: 'Untertitel ist erforderlich',
            },
          ]}
        >
          {this.renderTextArea({ placeholder: 'Kurzbeschreibung' })}
        </FormItem>

        <FormItem
          name="description"
          label={
            <AddFormItemLabel>
              {this.state.isEvent ? 'Eventbeschreibung' : 'Projektbeschreibung'}
            </AddFormItemLabel>
          }
          required={false}
          rules={[
            {
              required: true,
              message: 'Bitte geben Sie eine Beschreibung ein.',
            },
          ]}
          validateTrigger="onBlur"
        >
          <RichText
            name="description"
            getContent={this.getContent}
            placeholder={
              this.state.isEvent
                ? 'Beschreibe dein Event...'
                : 'Beschreibe dein Projekt...'
            }
          />
        </FormItem>
      </>
    );
  };

  renderLinkSection = () => {
    const { projectId } = this.props;
    const projectType = this.getProjectTypeLabel();

    return (
      <>
        <H2>Persönlicher Link zum {projectType}</H2>
        <p>
          Du kannst einen persönlichen Link zum {projectType} erstellen. So wird
          das Teilen noch einfacher!
        </p>

        <LinkInput
          projectId={projectId}
          form={projectId ? undefined : this.formRef.current}
        />
      </>
    );
  };

  renderFormContent = () => (
    <div>
      <FormWrapper>
        <>
          {this.renderTitleField()}
          {this.renderCampaignDetailsFields()}
          {this.renderStoryFields()}
          {this.renderLinkSection()}
        </>
      </FormWrapper>
    </div>
  );

  renderActions = () => {
    const { editProject } = this.props;

    return (
      <FormItem>
        <SpaceBetween>
          <SecondaryButton
            type="button"
            minWidth="120px"
            onClick={this.onReturn}
          >
            {editProject ? (
              <p>Zurück zur Projektübersicht</p>
            ) : (
              <>
                <ArrowLeftOutlined />
                <p>Zurück</p>
              </>
            )}
          </SecondaryButton>

          <PrimaryButtonV2
            type="primary"
            htmltype="submit"
            minWidth="120px"
            id="submit-button"
          >
            {editProject ? (
              <p>Speichern*</p>
            ) : (
              <>
                <p>Weiter</p>
                <ArrowRightOutlined />
              </>
            )}
          </PrimaryButtonV2>
        </SpaceBetween>
      </FormItem>
    );
  };

  renderForm = () => {
    const { project, projectId, setCanMove } = this.props;
    const initialValues = projectId
      ? pickBy(project.basicInfo, identity)
      : {};

    return (
      <Form
        {...formLayout}
        ref={this.formRef}
        onChange={() => setCanMove(false)}
        name="project-details"
        onFinish={this.onFinish}
        validateMessages={formValidateMessages}
        initialValues={initialValues}
      >
        {this.renderFormContent()}
        {this.props.editProject && <AddSaveHint />}
        {this.renderActions()}
      </Form>
    );
  };

  render() {
    const { project, loading, projectId } = this.props;
    const { creating } = this.state;
    const shouldRenderForm = !projectId || (!isEmpty(project) && !loading);

    return (
      <Container className="" style={{ maxWidth: 600 }}>
        <Spin spinning={projectId ? loading : creating}>
          <H1>Dein {this.getProjectTypeLabel()}</H1>
          {shouldRenderForm && this.renderForm()}
        </Spin>
      </Container>
    );
  }
}

const mapStateToProps = (state) => {
  const { currentStep, project, loading, errors, isUniq } = state.projects;
  return {
    currentStep,
    project,
    loading,
    errors,
    isUniq,
  };
};

const mapDispatchToProps = (dispatch) => ({
  handleSteps: (step) => dispatch(ProjectsActions.handleSteps(step)),
  checkUrl: (url) => dispatch(ProjectsActions.checkUrl(url)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BasicInformation);
